<template>
  <v-container fluid>
    <v-card>
      <v-toolbar color="primary" density="comfortable">
        <v-toolbar-title class="text-white">Laveries</v-toolbar-title>
        <v-spacer />
        <div class="d-flex ga-2">
          <TableHeaderButtons
            :attr="toolTipText"
            @add="add"
            @remove="remove"
            @reload="reloadData"
            @export="exportTable"
            @search="updateSearchPanel"
            :has-selected-lines="selectedLaverieIds.length > 0"
          />
        </div>
      </v-toolbar>
      <v-row class="px-4 py-2" align="center" density="compact">
        <v-col cols="12">
          <div class="d-flex flex-wrap ga-4">
            <v-switch
              v-model="signed"
              label="Signé"
              color="success"
              density="compact"
              inset
              hide-details
            />
            <v-switch
              v-model="inProgress"
              label="En cours"
              color="info"
              density="compact"
              inset
              hide-details
            />
            <v-switch
              v-model="lost"
              label="Perdu"
              color="error"
              density="compact"
              inset
              hide-details
            />
            <v-switch
              v-model="terminated"
              label="Résilié"
              color="warning"
              density="compact"
              inset
              hide-details
            />
          </div>
        </v-col>
      </v-row>
      <Table
        :loading="loading"
        :searchField="true"
        :columns="columns"
        :items="laveries"
        :showSelect="true"
        storageKey="laundryTablePaginationState"
        @edit="edit"
        @rowClicked="rowClicked"
        @selectionChanged="selectionChanged"
      >
        <!-- Products still to be assigned to a signed contract -->
        <template #item.missingProductCount="{ item }">
          <v-chip v-if="item.missingProductCount > 0" size="small" color="warning">
            {{ item.missingProductCount }} à affecter
          </v-chip>
        </template>
      </Table>
    </v-card>

    <v-overlay v-model="loading" class="align-center justify-center">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>

    <SearchPanel
      :rightDrawer="rightDrawer"
      @cancelSearch="cancelSearch"
      @searchData="searchLaveries"
    >
      <v-row>
        <v-col cols="11" offset="1">
          <v-text-field label="Nom" v-model="searchFilter.contain.name" />
        </v-col>
        <v-col cols="11" offset="1">
          <v-text-field
            label="Gestionnaire"
            v-model="searchFilter.contain.gestionnaire"
          />
        </v-col>
        <v-col cols="11" offset="1">
          <v-text-field label="Telephone" v-model="searchFilter.contain.telephone" />
        </v-col>
        <v-col cols="11" offset="1">
          <v-text-field label="Courriel" v-model="searchFilter.contain.email" />
        </v-col>
      </v-row>
    </SearchPanel>

    <ConfirmDialog
      v-model="dialog"
      dialogTitle="Suppression"
      dialogText="Voulez-vous supprimer les laveries sélectionnées?"
      @confirm="onConfirm"
      @cancel="onCancel"
      confirm-button-text="Confirmer"
      cancel-button-text="Annuler"
    />
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { useRouter } from "vue-router";

import Table from "@/components/Table.vue";
import TableHeaderButtons from "@/components/TableHeaderButtons.vue";
import SearchPanel from "@/components/SearchPanel.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";

import {
  buildSearchFilters,
  buildJsonServerQuery,
  clearSearchFilters,
} from "@/utils/app-util";

import { useAppStore } from "@/store/app";
import { createAndSaveDocument } from "@/utils/docx-util";
import { getLaverieExport, Laverie, TableColumn } from "@/types";
import { deleteLaverie, getFilteredLaveries, getLaverieById } from "@/utils/laverie-api";
import { ContratStatus } from "@/types";

const router = useRouter();
const appStore = useAppStore();

const dialog = ref(false);
const loading = ref(false);
const showSearchPanel = ref(false);

const inProgress = ref(false);
const signed = ref(true);
const lost = ref(false);
const terminated = ref(false);

const selectedLaverieIds = ref<string[]>([]);
const laveries = ref<Laverie[]>([]);

const searchFilter = ref({
  contain: {
    name: "",
    gestionnaire: "",
    telephone: "",
    email: "",
  },
});

let query = "";

const toolTipText = computed(() => "une laverie");

const rightDrawer = computed({
  get: () => showSearchPanel.value,
  set: (v: boolean) => (showSearchPanel.value = v),
});

const columns: TableColumn[] = [
  { title: "Nom", key: "name" },
  { title: "N° Contrat", key: "numeroDeContrat" },
  { title: "Gestionnaire", key: "gestionnaire" },
  { title: "Lavage(€)", key: "tarifLavage" },
  { title: "Sechage(€)", key: "tarifSechage" },
  { title: "Lessive(€)", key: "tarifLessive" },
  { title: "Redevance(%)", key: "tauxDeRedevance" },
  { title: "Durée Du Contrat", key: "dureeDuContrat" },
  { title: "Code Tournée", key: "codeTournee" },
  { title: "Type Contrat", key: "typeContrat" },
  { title: "Date", key: "dateDeDemarrage" },
  { title: "Status", key: "contratStatus" },
  { title: "Produits", key: "missingProductCount", align: "center" },
  { title: "", key: "actions", sortable: false },
];

watch([inProgress, signed, lost, terminated], () => {
  saveFiltersToLocalStorage();
  fetchLaveries();
});

function selectionChanged(selected: string[]) {
  selectedLaverieIds.value = [...selected];
}

function rowClicked(item: Laverie) {
  router.push(`laveries/${item.id}`);
}

function edit(item: Laverie) {
  router.push(`laveries/${item.id}`);
}

function add() {
  router.push("NewLaverie");
}

function remove() {
  dialog.value = true;
}

async function onConfirm() {
  dialog.value = false;
  loading.value = true;
  try {
    await deleteLaverie(selectedLaverieIds.value);
    await fetchLaveries();
    selectedLaverieIds.value = [];
    appStore.sendSuccessNotice("Laverie has been removed.");
  } finally {
    loading.value = false;
  }
}

function onCancel() {
  dialog.value = false;
}

function searchLaveries() {
  showSearchPanel.value = false;
  buildSearchFilters(searchFilter.value);
  query = buildJsonServerQuery(searchFilter.value);
}

function reloadData() {
  query = "";
  fetchLaveries();
}

const exportTable = async () => {
  if (loading.value) {
    return;
  }

  const laverieIds = [...selectedLaverieIds.value];
  if (!laverieIds.length) {
    return;
  }

  try {
    loading.value = true;
    const selectedLaveries = await Promise.all(
      laverieIds.map((id) => getLaverieById(id))
    );

    createAndSaveDocument(
      "export_laveries",
      "../templates/ExportLaveriesTemplate.docx",
      getLaverieExport(selectedLaveries)
    );
  } finally {
    loading.value = false;
  }
};

function updateSearchPanel() {
  showSearchPanel.value = !showSearchPanel.value;
}

function cancelSearch() {
  showSearchPanel.value = false;
}

async function fetchLaveries() {
  loading.value = true;
  try {
    const statuses: ContratStatus[] = [];
    if (inProgress.value) statuses.push(ContratStatus.EN_COURS);
    if (signed.value) statuses.push(ContratStatus.SIGNER);
    if (lost.value) statuses.push(ContratStatus.PERDU);
    if (terminated.value) statuses.push(ContratStatus.RESILIER);

    laveries.value = statuses.length ? await getFilteredLaveries(statuses) : [];
  } finally {
    loading.value = false;
  }
}

function saveFiltersToLocalStorage() {
  localStorage.setItem(
    "laverieListFilters",
    JSON.stringify({
      inProgress: inProgress.value,
      signed: signed.value,
      lost: lost.value,
      terminated: terminated.value,
    })
  );
}

function loadFiltersFromLocalStorage() {
  const saved = localStorage.getItem("laverieListFilters");
  if (!saved) return;

  try {
    const f = JSON.parse(saved);
    inProgress.value = !!f.inProgress;
    signed.value = !!f.signed;
    lost.value = !!f.lost;
    terminated.value = !!f.terminated;
  } catch {
    signed.value = true;
  }
}

onMounted(() => {
  loadFiltersFromLocalStorage();
  fetchLaveries();
});
</script>
<style scoped>
.title {
  font-size: 1.5rem;
  font-weight: bold;
}
</style>
